import { useState, useEffect, useRef, useCallback } from "react";
import { systemApi } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowsClockwise, CloudArrowDown, ArrowCounterClockwise, CheckCircle, WarningCircle, GitCommit, X, Database, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";

const fmt = (d) => { try { return format(new Date(d), "dd MMM yyyy, HH:mm"); } catch { return d || "—"; } };
const short = (sha) => (sha || "").slice(0, 7);
const RUNNING = ["running", "restarting"];
const STATE_LABEL = { success: "Completed", failed: "Failed", failed_restored: "Failed — previous version restored", running: "In progress", restarting: "Restarting" };

export default function SystemUpdateTab() {
  const [status, setStatus] = useState(null);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'apply' | 'merge' | 'rollback'
  const [log, setLog] = useState("");
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await systemApi.updateStatus();
      setStatus(data);
      if (data.lastCheck && !check) setCheck(data.lastCheck);
      return data;
    } catch { return null; }
  }, [check]);

  const pollJob = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await systemApi.updateLog();
        setLog(data.log);
        if (data.job && !RUNNING.includes(data.job.state)) {
          clearInterval(pollRef.current); pollRef.current = null;
          const fresh = await load();
          if (data.job.state === "success") toast.success(data.job.mode === "rollback" ? "Rolled back successfully" : "System updated successfully");
          else toast.error(STATE_LABEL[data.job.state] || "Update failed");
          if (fresh) setCheck(fresh.lastCheck);
        }
      } catch { /* server may be restarting — keep polling */ }
    }, 3000);
  }, [load]);

  useEffect(() => {
    load().then((d) => {
      if (!d?.job) return;
      systemApi.updateLog().then(({ data }) => setLog(data.log)).catch(() => {});
      if (RUNNING.includes(d.job.state)) pollJob();
    });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runCheck = async () => {
    setChecking(true);
    try {
      const { data } = await systemApi.updateCheck();
      setCheck(data);
      toast[data.upToDate ? "success" : "info"](data.upToDate ? "You're on the latest version" : "New version available");
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed"); }
    finally { setChecking(false); }
  };

  const onConfirmed = async (password) => {
    const mode = confirm;
    if (mode?.restore) {
      try {
        const { data } = await systemApi.backupRestore(mode.restore, password);
        setConfirm(null);
        toast.success(`Database restored (${data.documents} records). Safety copy: ${data.safetyBackup}`);
        await load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
      return;
    }
    try {
      await (mode === "apply" ? systemApi.updateApply(password) : mode?.merge ? systemApi.updateMerge(mode.merge, password) : systemApi.updateRollback(password));
      setConfirm(null);
      setLog("");
      toast.success(mode === "apply" ? "Update started" : mode?.merge ? "Merge started" : "Rollback started");
      await load();
      pollJob();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };

  if (!status) return <div className="h-40 bg-muted rounded-xl animate-pulse" />;
  const job = status.job;
  const busy = job && RUNNING.includes(job.state);

  return (
    <div className="space-y-5" data-testid="system-update-tab">
      <ServerCard status={status} />
      <CheckCard check={check} checking={checking} onCheck={runCheck} busy={busy} enabled={status.enabled} onApply={() => setConfirm("apply")} onMerge={(branches) => setConfirm({ merge: branches })} />
      {job && <JobCard job={job} log={log} />}
      <HistoryCard history={status.history} rollbackTarget={status.rollbackTarget} busy={busy} enabled={status.enabled} onRollback={() => setConfirm("rollback")} />
      <BackupsCard backups={status.backups} recipients={status.emailRecipients} busy={busy} onChanged={load} onRestore={(name) => setConfirm({ restore: name })} />
      {confirm && <PasswordDialog mode={confirm.restore ? "restore" : confirm.merge ? "merge" : confirm} target={confirm.restore || (confirm.merge ? confirm.merge.join(", ") : confirm === "rollback" ? status.rollbackTarget : check?.latest?.sha)} onCancel={() => setConfirm(null)} onConfirm={onConfirmed} />}
    </div>
  );
}

function ServerCard({ status }) {
  const c = status.current;
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="update-server-card">
      <h2 className="font-heading text-lg font-semibold">This server</h2>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div><p className="text-xs text-muted-foreground">Running version</p>
          <p className="font-mono font-semibold" data-testid="update-current-sha">{c ? short(c.sha) : "Unknown (not linked to GitHub yet)"}</p>
          {c && <p className="text-xs text-muted-foreground truncate">{c.message} · {fmt(c.date)}</p>}</div>
        <div><p className="text-xs text-muted-foreground">Source</p>
          <p className="font-mono text-xs break-all">{status.config.repo || "Not set"} · {status.config.branch}</p>
          <p className="text-xs text-muted-foreground">{status.config.tokenSet ? "Access token saved" : "No access token"}</p></div>
      </div>
      {!status.enabled && <Note tone="amber" testid="update-disabled-note">Updates are switched off on this server. On your production server add <code>SYSTEM_UPDATE_ENABLED=true</code> and <code>UPDATE_RESTART_CMD=…</code> (e.g. <code>sudo systemctl restart utheri-backend</code>) to <code>backend/.env</code>, then restart once.</Note>}
      {status.enabled && !status.restartConfigured && <Note tone="amber">No restart command set (<code>UPDATE_RESTART_CMD</code>) — after an update you'll need to restart the app yourself.</Note>}
      {status.localChanges > 0 && <Note tone="amber">{status.localChanges} file(s) were edited directly on this server. Updating will overwrite them.</Note>}
    </div>
  );
}

function CheckCard({ check, checking, onCheck, busy, enabled, onApply, onMerge }) {
  const [showFiles, setShowFiles] = useState(false);
  const [selected, setSelected] = useState([]);
  const pending = check && !check.upToDate;
  const branches = check?.branches || [];
  const ready = branches.filter((branch) => branch.status === "ready");
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="update-check-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold">Available updates</h2>
          <p className="text-xs text-muted-foreground">{check ? `Last checked ${fmt(check.checkedAt)} · checks automatically every day` : "Not checked yet"}</p>
        </div>
        <button onClick={onCheck} disabled={checking} data-testid="update-check-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-secondary border border-border text-sm font-medium active:scale-95 disabled:opacity-60">
          <ArrowsClockwise className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} /> {checking ? "Checking…" : "Check for updates"}
        </button>
      </div>
      {check?.upToDate && <Note tone="green" testid="update-uptodate">This server is running the latest version ({short(check.latest.sha)}).</Note>}
      {pending && (
        <>
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 text-sm" data-testid="update-available">
            <p className="font-semibold">New version {short(check.latest.sha)} — {check.latest.message}</p>
            <p className="text-xs text-muted-foreground">{check.unknownBase ? "This server's version isn't on GitHub, so showing the latest changes. Updating will make it match GitHub exactly." : `${check.behindBy} change(s) since your current version`}</p>
          </div>
          <ul className="divide-y divide-border border border-border rounded-xl max-h-72 overflow-y-auto" data-testid="update-commit-list">
            {check.commits.map((c) => (
              <li key={c.sha} className="px-3 py-2 flex gap-2 text-sm">
                <GitCommit className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0"><p className="truncate">{c.message}</p><p className="text-[11px] text-muted-foreground">{short(c.sha)} · {c.author} · {fmt(c.date)}</p></div>
              </li>
            ))}
          </ul>
          {check.files?.length > 0 && (
            <div>
              <button onClick={() => setShowFiles(!showFiles)} className="text-xs text-primary underline" data-testid="update-files-toggle">{showFiles ? "Hide" : "Show"} {check.files.length} changed file(s)</button>
              {showFiles && <ul className="mt-2 text-xs font-mono space-y-0.5 max-h-56 overflow-y-auto">{check.files.map((f) => <li key={f.filename} className="truncate"><span className="text-emerald-700">+{f.additions}</span> <span className="text-red-700">-{f.deletions}</span> {f.filename}</li>)}</ul>}
            </div>
          )}
          <button onClick={onApply} disabled={busy || !enabled} data-testid="update-apply-btn" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm active:scale-95 disabled:opacity-50">
            <CloudArrowDown className="w-5 h-5" /> Accept & update system
          </button>
        </>
      )}
      {check && branches.length > 0 && (
        <div className="border border-border rounded-xl p-3 space-y-3" data-testid="update-merge-plan">
          <div><p className="font-semibold text-sm">Merge feature branches into {check.branch}</p><p className="text-xs text-muted-foreground">Only branches ahead of production can be merged here. The suggested order respects branch ancestry.</p></div>
          {ready.length > 0 ? <div className="space-y-2">{ready.map((branch) => <label key={branch.name} className="flex items-start gap-2 text-sm cursor-pointer"><input type="checkbox" checked={selected.includes(branch.name)} onChange={(e) => setSelected(e.target.checked ? [...selected, branch.name] : selected.filter((name) => name !== branch.name))} disabled={busy || !enabled} /><span><span className="font-mono">{branch.name}</span> <span className="text-muted-foreground">({branch.aheadBy} commit{branch.aheadBy === 1 ? "" : "s"} ahead){branch.mergeAfter?.length ? ` · after ${branch.mergeAfter.join(", ")}` : ""}</span></span></label>)}</div> : <p className="text-xs text-muted-foreground">No merge-ready feature branches found.</p>}
          {branches.filter((branch) => branch.status === "needs_manual_resolution").map((branch) => <p key={branch.name} className="text-xs text-amber-700"><span className="font-mono">{branch.name}</span> has diverged and needs conflicts resolved before it can be merged.</p>)}
          <button onClick={() => onMerge(selected)} disabled={!selected.length || busy || !enabled} data-testid="update-merge-btn" className="w-full py-2.5 rounded-xl border border-primary text-primary font-semibold text-sm disabled:opacity-50">Merge selected branches into {check.branch}</button>
        </div>
      )}
    </div>
  );
}

function JobCard({ job, log }) {
  const running = RUNNING.includes(job.state);
  const ok = job.state === "success";
  const downloadLog = async () => {
    try {
      const { data } = await systemApi.updateLogExport();
      const url = URL.createObjectURL(new Blob([data], { type: "text/plain" }));
      const link = document.createElement("a");
      link.href = url; link.download = `utheri-update-log-${short(job.targetSha) || "latest"}.log`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not export the update log"); }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="update-job-card">
      <div className="flex items-center gap-2">
        {running ? <ArrowsClockwise className="w-5 h-5 text-primary animate-spin" /> : ok ? <CheckCircle className="w-5 h-5 text-emerald-600" weight="fill" /> : <WarningCircle className="w-5 h-5 text-red-600" weight="fill" />}
        <p className="font-semibold text-sm" data-testid="update-job-state">{job.mode === "rollback" ? "Rollback" : job.mode === "merge" ? "Merge" : "Update"} to {short(job.targetSha)}: {STATE_LABEL[job.state] || job.state}</p>
      </div>
      <p className="text-xs text-muted-foreground">{job.step}{running && " — the site may be briefly unavailable while it restarts."}</p>
      {log && <><button onClick={downloadLog} data-testid="update-log-export-btn" className="inline-flex items-center gap-1 text-xs text-primary underline"><DownloadSimple className="w-3.5 h-3.5" /> Export log</button><pre className="bg-slate-950 text-slate-100 text-[11px] rounded-lg p-3 max-h-64 overflow-auto whitespace-pre-wrap" data-testid="update-log">{log}</pre></>}
    </div>
  );
}

function HistoryCard({ history, rollbackTarget, busy, enabled, onRollback }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="update-history-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-heading text-lg font-semibold">Update history</h2>
        {rollbackTarget && (
          <button onClick={onRollback} disabled={busy || !enabled} data-testid="update-rollback-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-300 text-red-700 text-xs font-semibold active:scale-95 disabled:opacity-50">
            <ArrowCounterClockwise className="w-4 h-4" /> Roll back to {short(rollbackTarget)}
          </button>
        )}
      </div>
      {history.length === 0 ? <p className="text-sm text-muted-foreground">No updates yet.</p> : (
        <ul className="divide-y divide-border text-sm">
          {history.map((h) => (
            <li key={h.jobId} className="py-2 flex items-center justify-between gap-3" data-testid={`update-history-${h.jobId}`}>
              <div className="min-w-0"><p className="truncate">{h.mode === "rollback" ? "Rollback" : "Update"} {short(h.fromSha) || "—"} → {short(h.toSha)} · {h.summary}</p>
                <p className="text-[11px] text-muted-foreground">{fmt(h.startedAt)} · by {h.startedBy}</p></div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${h.state === "success" ? "bg-emerald-100 text-emerald-700" : RUNNING.includes(h.state) ? "bg-sky-100 text-sky-700" : "bg-red-100 text-red-700"}`}>{STATE_LABEL[h.state] || h.state}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BackupsCard({ backups = [], recipients = [], busy, onChanged, onRestore }) {
  const [working, setWorking] = useState(false);
  const backupNow = async () => {
    setWorking(true);
    try { const { data } = await systemApi.backupNow(); toast.success(`Backup saved (${data.documents} records)`); await onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setWorking(false); }
  };
  const download = async (name) => {
    try {
      const { data } = await systemApi.backupDownload(name);
      const url = URL.createObjectURL(new Blob([data], { type: "application/gzip" }));
      const a = document.createElement("a"); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="update-backups-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-lg font-semibold">Database backups</h2>
          <p className="text-xs text-muted-foreground">Taken automatically before every update or rollback · last 10 kept</p>
        </div>
        <button onClick={backupNow} disabled={working || busy} data-testid="backup-now-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-secondary border border-border text-xs font-semibold active:scale-95 disabled:opacity-60">
          <Database className="w-4 h-4" /> {working ? "Backing up…" : "Back up now"}
        </button>
      </div>
      {backups.length === 0 ? <p className="text-sm text-muted-foreground">No backups yet.</p> : (
        <ul className="divide-y divide-border text-sm">
          {backups.map((b) => (
            <li key={b.name} className="py-2 flex items-center justify-between gap-3" data-testid={`backup-row-${b.name}`}>
              <div className="min-w-0"><p className="truncate">{b.reason}</p>
                <p className="text-[11px] text-muted-foreground">{fmt(b.createdAt)} · {b.documents} records · {(b.sizeBytes / 1024).toFixed(0)} KB · code {short(b.codeSha) || "—"}</p></div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => download(b.name)} data-testid={`backup-download-${b.name}`} className="p-2 rounded-lg border border-border hover:bg-secondary" title="Download"><DownloadSimple className="w-4 h-4" /></button>
                <button onClick={() => onRestore(b.name)} disabled={busy} data-testid={`backup-restore-${b.name}`} className="px-2.5 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs font-semibold disabled:opacity-50">Restore</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="update-email-recipients">
        <EnvelopeSimple className="w-3.5 h-3.5 shrink-0" />
        {recipients.length ? `Update emails go to: ${recipients.join(", ")}` : "No alert email set — add one in Update source settings or give a Super Admin an email address."}
      </p>
    </div>
  );
}

function PasswordDialog({ mode, target, onCancel, onConfirm }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    await onConfirm(inputRef.current?.value || "");
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="update-password-dialog">
      <form onSubmit={submit} className="bg-card rounded-xl border border-border w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">{{ apply: "Confirm update", merge: "Confirm production merge", rollback: "Confirm rollback", restore: "Restore database?" }[mode]}</h3>
          <button type="button" onClick={onCancel} className="text-muted-foreground" data-testid="update-password-cancel"><X className="w-5 h-5" /></button>
        </div>
        {mode === "restore" ? (
          <p className="text-sm text-muted-foreground">All current data will be replaced with backup <span className="font-mono text-xs break-all">{target}</span>. Payments and changes made after it will be lost (a safety copy of today's data is saved first). Enter your password to continue.</p>
        ) : (
          <p className="text-sm text-muted-foreground">A database backup is saved first. {mode === "apply" ? "The system will then download" : mode === "merge" ? "The system will merge" : "The system will then return to"} <span className="font-mono">{mode === "merge" ? target : short(target)}</span>{mode === "merge" ? ", publish the production branch, install packages and restart." : ", install packages and restart."} Enter your password to continue.</p>
        )}
        <input ref={inputRef} type="password" autoFocus placeholder="Your password" data-testid="update-password-input" className="w-full px-3 py-2 text-base bg-card border border-input rounded-xl" />
        <button type="submit" disabled={busy} data-testid="update-password-confirm" className={`w-full py-2.5 rounded-xl font-semibold text-sm active:scale-95 disabled:opacity-60 ${mode === "restore" ? "bg-red-600 text-white" : "bg-primary text-primary-foreground"}`}>
          {busy ? "Working…" : { apply: "Update now", merge: "Merge into production", rollback: "Roll back now", restore: "Restore database" }[mode]}
        </button>
      </form>
    </div>
  );
}

function Note({ tone, children, testid }) {
  const cls = tone === "green" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900";
  return <div className={`border rounded-xl p-3 text-xs ${cls}`} data-testid={testid}>{children}</div>;
}
